import {
  Bars3Icon,
  ChevronDownIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { MapPinIcon as MapPinSolid } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { useReduxStore } from '../hooks/useReduxStore';
import { LogoutAction } from '../store/Actions/action';
import { useLocation, useNavigate } from 'react-router-dom';
import UserAvatar from './UserAvatar';
import { useWeatherLocation } from '../contexts/WeatherLocationContext';

export default function Header({ onToggleSidebar }) {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { getState, dispatch } = useReduxStore();
  const { userData, token } = getState('AUTH');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const {
    displayName,
    isLive,
    isDefault,
    loading: locationLoading,
    error: locationError,
    activateCurrentLocation,
  } = useWeatherLocation();

  const logout = () => {
    navigate('/', { replace: true });
    dispatch(LogoutAction());
  };
  const manageProfile = () => {
    navigate('/Dashboard/Profile');
  };

  const handleUseCurrentLocation = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (locationLoading) return;
    void activateCurrentLocation();
  };

  return (
    <header className="text-white sticky top-0 z-30">
      {/* Row 1 */}
      <div className="flex items-center justify-between px-3 py-2 sm:px-6 sm:py-3">
        {/* Left: Toggle + Active Location */}
        <div className="flex items-center gap-3 sm:gap-6 flex-1 min-w-0">
          <button onClick={onToggleSidebar} className="shrink-0">
            <Bars3Icon className="h-6 w-6 text-white" />
          </button>

          {/* Active location strip */}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {isLive ? (
              <MapPinSolid className="h-4 w-4 shrink-0 text-green-400" />
            ) : isDefault ? (
              <MapPinSolid className="h-4 w-4 shrink-0 text-amber-400" />
            ) : (
              <MapPinIcon className="h-4 w-4 shrink-0 text-gray-300" />
            )}
            <span
              className="min-w-0 flex-1 truncate text-sm font-medium text-white sm:max-w-xs sm:flex-none"
              title={displayName}
            >
              {displayName}
            </span>
          </div>

          {/* "Use Current Location" CTA — shown when not already using live GPS */}
          {!isLive && (
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              disabled={locationLoading}
              className="hidden sm:flex items-center gap-1.5 shrink-0 text-xs font-medium text-primaryBlue bg-white/90 hover:bg-white px-3 py-1.5 rounded-full transition disabled:opacity-50"
            >
              <MapPinSolid className="h-3.5 w-3.5 text-primaryBlue" />
              {locationLoading ? 'Locating…' : 'Use Current Location'}
            </button>
          )}

          {/* Mobile: just the GPS icon CTA */}
          {!isLive && (
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              disabled={locationLoading}
              className="sm:hidden shrink-0 p-1.5 rounded-full bg-white/15 hover:bg-white/25 transition disabled:opacity-50"
              aria-label="Use current location"
            >
              <MapPinSolid className="h-4 w-4 text-white" />
            </button>
          )}
        </div>

        {/* Right: User Info + Dropdown */}
        <div className="flex items-center gap-4 sm:gap-6 ml-2 sm:ml-4 shrink-0">
          <div
            className="flex items-center gap-2 cursor-pointer relative group"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <UserAvatar
              name={userData?.name}
              size="w-8 h-8 sm:w-10 sm:h-10"
              className="rounded-full object-cover border border-white"
            />
            <span className="text-sm hidden sm:inline">{userData?.name}</span>
            <ChevronDownIcon className="h-4 w-4" />

            <div
              className={`absolute top-8 right-0 bg-white text-black p-2 shadow-xl rounded ${
                dropdownOpen ? 'block' : 'hidden md:group-hover:block'
              } z-50 w-36`}
            >
              {token ? (
                <div onClick={manageProfile} className="hover:bg-gray-100 px-3 py-1 rounded">
                  Profile
                </div>
              ) : null}
              <div onClick={logout} className="hover:bg-gray-100 px-3 py-1 rounded">
                Logout
              </div>
            </div>
          </div>
        </div>
      </div>

      {locationError && (
        <div className="mx-3 sm:mx-6 mt-1 px-3 py-2 rounded-lg bg-red-500/20 border border-red-400/40 text-red-100 text-xs sm:text-sm">
          {locationError}
        </div>
      )}

      {/* Row 2 — spacer (preserved for layout) */}
      {routerLocation?.pathname !== '/Admin/Dashboard/AdminChat' ? (
        <div className="hidden md:flex items-center justify-between px-4 py-4 pt-4">
          <div className="flex-1 border-b border-borderGray" />
        </div>
      ) : null}
    </header>
  );
}
